import { CartUpdateEvent, ThemeEvents } from '@theme/events';

const SOURCE = 'ab-kite-cart-sync';
const DELAYS = [600, 900, 1500];
let generation = 0;
let controller;

function signature(cart) {
  if (!Array.isArray(cart?.items)) return null;
  return JSON.stringify([
    cart.total_price,
    cart.item_count,
    cart.items.map((item) => [item.key, item.quantity, item.final_line_price, item.selling_plan_allocation?.selling_plan?.id]),
  ]);
}

function renderedMatches(cart) {
  const components = Array.from(document.querySelectorAll('cart-items-component'));
  if (!components.length) return true;
  return components.every((component) => {
    const wrapper = component.querySelector('[data-ab-cart-total]');
    if (!wrapper || Number(wrapper.dataset.abCartTotal) !== cart.total_price ||
        Number(wrapper.dataset.abCartCount) !== cart.item_count) return false;
    const rows = Array.from(component.querySelectorAll('[data-key][data-ab-quantity]'));
    return rows.length === cart.items.length && rows.every((row, index) => {
      const item = cart.items[index];
      return row.dataset.key === item.key &&
        Number(row.dataset.abQuantity) === item.quantity &&
        Number(row.dataset.abFinalLinePrice) === item.final_line_price &&
        (row.dataset.abSellingPlan || '') === String(item.selling_plan_allocation?.selling_plan?.id || '');
    });
  });
}

function cancelPending() {
  generation += 1;
  controller?.abort();
}

async function readJSON(url, signal) {
  const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal });
  if (!response.ok) throw new Error('Cart synchronization unavailable');
  return response.json();
}

function renderCart(cart, sections) {
  const drawers = Array.from(document.querySelectorAll('cart-drawer-component'));
  const states = drawers.map((drawer) => ({
    drawer,
    autoOpen: drawer.hasAttribute('auto-open'),
    open: Boolean(drawer.querySelector('dialog')?.open),
  }));
  const preparedSections = Object.fromEntries(Object.entries(sections).map(([id, html]) => {
    const fragment = new DOMParser().parseFromString(html, 'text/html');
    Array.from(fragment.querySelectorAll('cart-drawer-component')).forEach((drawer, index) => {
      const state = states[index];
      drawer.removeAttribute('auto-open');
      drawer.querySelector('dialog')?.toggleAttribute('open', state?.open ?? false);
    });
    return [id, fragment.documentElement.outerHTML];
  }));
  states.forEach(({ drawer }) => drawer.removeAttribute('auto-open'));
  try {
    document.dispatchEvent(new CartUpdateEvent(cart, SOURCE, {
      source: SOURCE,
      itemCount: cart.item_count,
      sections: preparedSections,
    }));
  } finally {
    Array.from(document.querySelectorAll('cart-drawer-component')).forEach((drawer, index) => {
      drawer.toggleAttribute('auto-open', states[index]?.autoOpen ?? false);
    });
    states.forEach(({ drawer, autoOpen }) => {
      if (!drawer.isConnected) drawer.toggleAttribute('auto-open', autoOpen);
    });
  }
}

async function reconcile(event) {
  if (event.detail?.sourceId === SOURCE || event.detail?.data?.didError) return;
  const resource = event.detail?.resource;
  // Gift apps can update the cart after the theme receives the original add response.
  // Check only after cart mutations; no background or page-load polling.
  cancelPending();
  const currentGeneration = generation;
  controller = new AbortController();
  const { signal } = controller;
  let previousSignature = signature(resource);
  const cartURL = `${window.Shopify?.routes?.root || '/'}cart.js`;
  try {
    for (const delay of DELAYS) {
      await new Promise((resolve) => setTimeout(resolve, delay));
      if (currentGeneration !== generation || signal.aborted) return;
      const cart = await readJSON(cartURL, signal);
      const nextSignature = signature(cart);
      if (nextSignature === null) return;
      if (nextSignature === previousSignature && renderedMatches(cart)) continue;
      const sectionIds = [...new Set(Array.from(document.querySelectorAll('cart-items-component[data-section-id]'), (node) => node.dataset.sectionId))].filter(Boolean).slice(0, 5);
      if (!sectionIds.length) return;
      const sectionURL = new URL(window.location.href);
      sectionURL.searchParams.set('sections', sectionIds.join(','));
      const sections = await readJSON(sectionURL, signal);
      if (sectionIds.some((id) => typeof sections[id] !== 'string' || !sections[id])) continue;
      const verifiedCart = await readJSON(cartURL, signal);
      if (currentGeneration !== generation || signal.aborted) return;
      if (signature(verifiedCart) !== nextSignature) continue;
      renderCart(verifiedCart, sections);
      previousSignature = nextSignature;
    }
  } catch (error) {
    if (error.name !== 'AbortError') console.warn('[Aussie Betta] Cart display synchronization skipped.');
  }
}

document.addEventListener(ThemeEvents.cartUpdate, reconcile);
document.addEventListener(ThemeEvents.discountUpdate, reconcile);
document.addEventListener(ThemeEvents.quantitySelectorUpdate, cancelPending, true);
document.addEventListener('submit', cancelPending, true);
document.addEventListener('click', (event) => {
  if (event.target instanceof Element && event.target.closest('cart-items-component button, cart-items-component input')) cancelPending();
}, true);
